import {
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { useWarehouseStore } from '@/stores/warehouse-store';
import { apiClient } from '@/api/client';
import {
  onAuthStateChanged,
  signInWithEmail,
  signInWithGoogle,
  signOut as firebaseSignOut,
  resetPassword,
  isFirebaseConfigured,
  getIdToken,
} from '@/lib/firebase';
import type { User } from '@/types';
import { withWarehouseRoles } from '@/lib/user-roles';
import { AuthContext, type AuthContextValue } from './AuthContext';

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [loading, setLoading] = useState(true);
  const [loggedOut, setLoggedOut] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const { user, isAuthenticated, login, logout } = useAuthStore();
  const navigate = useNavigate();

  const clearAuthError = useCallback(() => setAuthError(null), []);

  useEffect(() => {
    // In dev mode without Firebase, auto-login with real user from API
    if (import.meta.env.DEV && !isFirebaseConfigured()) {
      const fetchDevUser = async () => {
        try {
          // Fetch real user from API using the test user ID header
          const response = await apiClient.get<User>('/api/users/me');
          login(withWarehouseRoles(response.data), 'dev-token');
          if (response.data.warehouseAssignments) {
            useWarehouseStore
              .getState()
              .initFromUserProfile(
                response.data.warehouseAssignments,
                response.data.primaryWarehouseId ?? null,
                response.data.isAdmin,
              );
          }
        } catch (err) {
          console.error('Failed to fetch dev user, using mock:', err);
          setAuthError('API server not reachable — using mock user');
          // Fallback to mock user if API fails
          const mockUser: User = {
            id: 'dev-user-001',
            email: 'admin@example.com',
            name: 'Admin User',
            isAdmin: true,
            isAuditor: false,
            warehouseRoles: {},
            isActive: true,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };
          login(mockUser, 'dev-token');
        }
        setLoading(false);
      };

      if (!isAuthenticated) {
        fetchDevUser();
      } else {
        // Already authenticated from persisted store — unblock UI immediately,
        // then refresh user in background
        setLoading(false);
        fetchDevUser();
      }
      return;
    }

    // If already authenticated from persisted store, unblock UI immediately.
    // onAuthStateChanged will refresh the token and user profile in the background.
    if (isAuthenticated) {
      setLoading(false);
    }

    // Listen for Firebase auth state changes
    const unsubscribe = onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          // Fetch user profile from backend
          const response = await apiClient.get<User>('/api/users/me', {
            headers: { Authorization: `Bearer ${idToken}` },
          });
          login(withWarehouseRoles(response.data), idToken);
          if (response.data.warehouseAssignments) {
            useWarehouseStore
              .getState()
              .initFromUserProfile(
                response.data.warehouseAssignments,
                response.data.primaryWarehouseId ?? null,
                response.data.isAdmin,
              );
          }
          setAuthError(null);
        } catch (err: any) {
          console.error('Failed to fetch user profile:', err);
          // Classify the error for display
          if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED') {
            setAuthError('Cannot connect to the server. Is the API running?');
          } else if (err.code === 'ECONNABORTED') {
            setAuthError('Server is not responding. Please try again.');
          } else if (err.response?.status === 503) {
            setAuthError('Service temporarily unavailable. Please try again.');
          } else if (err.response?.status === 401) {
            const msg = err.response?.data?.message;
            if (msg === 'User not found') {
              setAuthError('Account not found. Contact your administrator.');
            } else if (msg === 'User account is deactivated') {
              setAuthError(
                'Your account has been deactivated. Contact your administrator.',
              );
            } else if (msg === 'Session expired') {
              setAuthError('Your session has expired. Please sign in again.');
            } else {
              setAuthError('Authentication failed. Please sign in again.');
            }
          } else if (err.response?.status === 403) {
            setAuthError('Login is not allowed from your location.');
          } else {
            setAuthError('Something went wrong. Please try again.');
          }
          logout();
        }
      } else {
        // Firebase has no user. If the persisted store has a WMS session token
        // (username-login path), the user IS logged in — Firebase is just not
        // their auth provider. Do not log them out.
        const { wmsToken, wmsTokenExpiresAt } = useAuthStore.getState();
        const wmsValid =
          !!wmsToken &&
          (!wmsTokenExpiresAt ||
            new Date(wmsTokenExpiresAt).getTime() > Date.now());
        if (!wmsValid) {
          logout();
        }
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [login, logout]);

  // Login-time IP allowlist gate. Server enforces only for password sign-in;
  // google.com bypasses. On block, sign out so the user isn't half-authed.
  const runFirebaseLoginGate = async () => {
    // Sanity-check that Firebase state propagated; the apiClient interceptor
    // attaches the actual Authorization header from `getIdToken()`.
    const token = await getIdToken();
    if (!token) {
      await firebaseSignOut();
      throw new Error('Could not obtain Firebase ID token after sign-in');
    }
    try {
      await apiClient.post('/api/auth/firebase-login', {});
    } catch (err) {
      await firebaseSignOut();
      throw err;
    }
  };

  const handleSignInWithEmail = async (email: string, password: string) => {
    await signInWithEmail(email, password);
    await runFirebaseLoginGate();
    // onAuthStateChanged will handle the rest
  };

  const handleSignInWithGoogle = async () => {
    await signInWithGoogle();
    await runFirebaseLoginGate();
    // onAuthStateChanged will handle the rest
  };

  const handleSignOut = useCallback(async () => {
    if (isFirebaseConfigured()) {
      await firebaseSignOut();
    }
    logout();
    setLoggedOut(true);
    navigate('/login');
  }, [logout, navigate]);

  const handlePasswordReset = async (email: string) => {
    await resetPassword(email);
  };

  const handleDevLogin = useCallback(() => {
    const mockUser: User = {
      id: 'e4e6d78e-bd4c-42c7-8fa3-bb570dc194a4',
      email: 'davidp@modularclosets.com',
      name: 'David Pollak',
      isAdmin: true,
      isAuditor: false,
      warehouseRoles: {},
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    login(mockUser, 'dev-token');
    setLoggedOut(false);
    navigate('/');
  }, [login, navigate]);

  const value: AuthContextValue = {
    loading,
    user,
    isAuthenticated,
    loggedOut,
    authError,
    signInWithEmailPassword: handleSignInWithEmail,
    signInWithGoogleAccount: handleSignInWithGoogle,
    signOut: handleSignOut,
    sendPasswordReset: handlePasswordReset,
    clearAuthError,
    devLogin: handleDevLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
